import Button from '@mui/material/Button';
import * as tinymce from 'tinymce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BillingGroup, MinBillingGroup } from 'common/models/BillingGroup';
import { Contact } from 'common/models/Contacts';
import {
  CreateBillingGroup,
  GetBillingGroupById,
  UpdateBillingGroup,
} from 'services/BillingGroupsService';
import TitleAndActionSummaryBar from 'components/SummaryBars/TitleAndActionSummaryBar';
import useObjectStateWithChangeTracker from 'hooks/UseObjectStateWithChangeTracker';
import useUnsavedChangesDialog from 'hooks/UseUnsavedChangesDialog';
import { Navigate } from 'react-router-dom';
import {
  BillingGroupToMinBillingGroup,
  DefaultMinBillingGroup,
  DefaultMinBillingGroupNoChanges,
} from 'util/Definitions/BillingGroup';
import Box from '@mui/material/Box';
import { DashboardLayoutComponent } from '@syncfusion/ej2-react-layouts';
import PanelWrapper from 'components/Dashboards/PanelWrapper';
import {
  BillingGroupRecordDashboardElementDefinition,
  BillingGroupRecordDashboardElementType,
} from 'common/models/Dashboard/EditLayout';
import EditableRichTextElement from '../EditableRichTextElement';
import EditableSingleFieldElement from '../EditableSingleFieldElement';
import ClientPicker from 'components/Pickers/ClientPicker';
import ContactPicker from 'components/Pickers/ContactPicker';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import {
  BillingGroupScreenLayoutSettings,
  DefaultBillingGroupRecordDashboardElements,
} from 'util/Definitions/ScreenLayouts/BillingGroup';
import { Link } from 'react-router-dom';
import { RegexIsPositiveInteger } from 'util/RegExUtils';
import moment from 'moment';
import Divider from '@mui/material/Divider';
import CountryPicker from '../../Pickers/CountryPicker';
import { Client } from '../../../common/models/Clients';
import { GetClientById } from '../../../services/ClientsService';

interface Props {
  billingGroupId?: number;
  clientId?: number;
  setSummaryBar?: (sb: JSX.Element) => void;
  loadingHandler?: (isLoading: boolean) => void;
  successHandler?: (message: string) => void;
  errorHandler?: (message: string) => void;
}

const { unitWidth, unitHeight, gapX, gapY, columns, mediaQueryMaxWidth } =
  BillingGroupScreenLayoutSettings;
const cellSpacing = [gapX, gapY];

export default function EditRecordScreenLayout({
  billingGroupId = 0,
  clientId = 0,
  setSummaryBar,
  loadingHandler,
  successHandler,
  errorHandler,
}: Props) {
  const [isFetchingBillingGroup, setIsFetchingBillingGroup] = useState(false);
  const [fetchedBillingGroup, setFetchedBillingGroup] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [fetchedLayoutConfig, setFetchedLayoutConfig] = useState(false);
  const [fetchingLayoutConfig, setFetchingLayoutConfig] = useState(false);
  const [existingBillingGroup, setExistingBillingGroup] = useState<BillingGroup | null>(null);

  const [isEditorDirty, setIsEditorDirty] = useState(false);
  const { state, init, change, updateInitial, hasChanges } =
    useObjectStateWithChangeTracker<MinBillingGroup>(
      DefaultMinBillingGroup,
      DefaultMinBillingGroupNoChanges
    );
  const editorRef = useRef<tinymce.Editor | null>(null);
  const [savedChanges, setSavedChanges] = useState(false);
  const [createdBillingGroupId, setCreatedBillingGroupId] = useState(0);
  const [screenResizedControl, setScreenResizedControl] = useState(false);
  const layoutRef = useRef<DashboardLayoutComponent | null>(null);
  const theme = useTheme();

  useEffect(() => {
    const setDefaultCreationValues = async () => {
      const tempState = { ...DefaultMinBillingGroup };

      if (clientId) {
        const client = await GetClientById(clientId);
        if (client) {
          tempState.clientID = clientId;
          tempState.address1 = client.address1 ?? '';
          tempState.address2 = client.address2 ?? '';
          tempState.address3 = client.address3 ?? '';
          tempState.suburb = client.suburb ?? '';
          tempState.state = client.state ?? '';
          tempState.postcode = client.postcode ?? '';
          tempState.country = client.countryCode ?? '';
        }
      }

      init(tempState);
    };
    billingGroupId === 0 && setDefaultCreationValues();
  }, [billingGroupId, clientId, init]);

  useEffect(() => {
    const getExistingClient = async () => {
      setIsFetchingBillingGroup(true);
      const res = await GetBillingGroupById(billingGroupId, errorHandler);
      if (res) setExistingBillingGroup(res);
      setFetchedBillingGroup(true);
      setIsFetchingBillingGroup(false);
    };
    billingGroupId && getExistingClient();
  }, [billingGroupId, errorHandler]);

  useEffect(() => {
    const getSavedState = async () => {
      setFetchingLayoutConfig(false);
      setFetchedLayoutConfig(true);
    };
    getSavedState();
  }, []);

  useEffect(
    () => loadingHandler && loadingHandler(isFetchingBillingGroup || fetchingLayoutConfig),
    [isFetchingBillingGroup, fetchingLayoutConfig, loadingHandler]
  );

  useEffect(() => {
    if (billingGroupId && existingBillingGroup && fetchedBillingGroup) {
      const mc = BillingGroupToMinBillingGroup(existingBillingGroup);
      init(mc);
      const api = editorRef.current;
      if (api) api.setContent(existingBillingGroup.description ?? '');
    }
  }, [billingGroupId, existingBillingGroup, fetchedBillingGroup, init]);

  const saveChangesHandler = useCallback(async () => {
    if (!state.name) {
      setShowValidation(true);
      errorHandler && errorHandler("Name can't be empty");
      return false;
    }

    if (state.clientID === 0) {
      setShowValidation(true);
      errorHandler && errorHandler('Client must be set');
      return false;
    }

    if (state.primaryContactID === 0) {
      setShowValidation(true);
      errorHandler && errorHandler('Primary Contact must be set');
      return false;
    }

    loadingHandler && loadingHandler(true);
    const editorApi = editorRef.current;
    const summaryContent = editorApi ? editorApi.getContent() : '';

    if (billingGroupId === 0) {
      const res = await CreateBillingGroup({ ...state, description: summaryContent }, errorHandler);
      if (!res) {
        loadingHandler && loadingHandler(false);
        return false;
      }
      setCreatedBillingGroupId(res.value);
    } else if (billingGroupId && (hasChanges || isEditorDirty)) {
      const res = await UpdateBillingGroup(
        billingGroupId,
        { ...state, description: summaryContent },
        errorHandler
      );
      if (!res) {
        loadingHandler && loadingHandler(false);
        return false;
      }
    }
    updateInitial();
    setIsEditorDirty(false);
    editorApi && editorApi.setDirty(false);
    loadingHandler && loadingHandler(false);
    setSavedChanges(true);
    return true;
  }, [
    billingGroupId,
    state,
    hasChanges,
    isEditorDirty,
    updateInitial,
    loadingHandler,
    errorHandler,
  ]);

  useEffect(() => {
    const actionButton = (
      <Button
        variant="contained"
        color="success"
        disabled={!hasChanges && !isEditorDirty}
        onClick={saveChangesHandler}
      >
        Save
      </Button>
    );
    if (billingGroupId === 0 && setSummaryBar) {
      const sb = (
        <TitleAndActionSummaryBar
          title="Billing Groups > Create"
          browserTabTitle="Create > Billing Groups"
          action={actionButton}
        />
      );
      setSummaryBar(sb);
    } else if (billingGroupId && setSummaryBar) {
      const goBackAction = (
        <Link to={`/billing-groups/${billingGroupId}`} style={{ textDecoration: 'none' }}>
          <Button variant="contained" color="error" sx={{ mr: '5px' }}>
            Cancel
          </Button>
        </Link>
      );
      const sb = (
        <TitleAndActionSummaryBar
          title="Billing Groups > Edit"
          browserTabTitle="Edit > Billing Groups"
          action={
            <>
              {goBackAction}
              {actionButton}
            </>
          }
        />
      );
      setSummaryBar(sb);
    }
  }, [billingGroupId, hasChanges, isEditorDirty, setSummaryBar, saveChangesHandler]);

  const onStringFieldChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      let val = value;
      if (name.startsWith('dtUDF') && value && !value.includes('T')) val += 'T00:00:00';
      change(name as keyof MinBillingGroup, val);
    },
    [change]
  );

  const onDateFieldChange = useCallback(
    (m: moment.Moment | null, fieldName: string) => {
      if (m && m.isValid()) {
        change(fieldName as keyof MinBillingGroup, m.format('YYYY-MM-DD'));
      } else if (m === null) change(fieldName as keyof MinBillingGroup, '');
    },
    [change]
  );

  const onDateTimeFieldChange = useCallback(
    (m: moment.Moment | null, fieldName: string) => {
      if (m && m.isValid()) {
        change(fieldName as keyof MinBillingGroup, m.format('YYYY-MM-DDTHH:mm'));
      } else if (m === null) change(fieldName as keyof MinBillingGroup, '');
    },
    [change]
  );

  const positiveIntegerNumberValidator = useCallback((value: string) => {
    return value === '' || RegexIsPositiveInteger(value);
  }, []);

  const onNumberFieldChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;

      const n = +value;
      if (isNaN(n) && value !== '') return;
      if (value.length > 11) return;

      const i = value.indexOf('.');
      if (value.length > 8 && i === -1) return;

      if (i >= 0) {
        const decimals = value.substring(i + 1);
        if (decimals.length > 2) {
          change(name as keyof MinBillingGroup, value.substring(0, i + 3));
          return;
        }
      }

      change(name as keyof MinBillingGroup, value);
    },
    [change]
  );

  const onNumericListFieldChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      change(name as keyof MinBillingGroup, +value);
    },
    [change]
  );

  const onContactChange = useCallback(
    (contact: Contact | null, field: 'primaryContactID') => {
      change(field, contact ? contact.id : 0);
    },
    [change]
  );

  const onClientChange = useCallback(
    (client: Client | null) => {
      change('clientID', client ? client.id : 0);
    },
    [change]
  );

  const onCountryChange = useCallback(
    (countryCode: string | null) => {
      if (countryCode === 'AU') change('state', '');
      change('country', countryCode ?? '');
    },
    [change]
  );

  const { unsavedChangesDialog, hasBlockedRoute } = useUnsavedChangesDialog(
    hasChanges || isEditorDirty,
    saveChangesHandler
  );

  const redirectBillingGroupId = useMemo(() => {
    if (billingGroupId !== 0) return billingGroupId;
    if (savedChanges && createdBillingGroupId) return createdBillingGroupId;
    return 0;
  }, [billingGroupId, createdBillingGroupId, savedChanges]);

  const elements = useMemo<BillingGroupRecordDashboardElementDefinition[]>(() => {
    if (!fetchedLayoutConfig) return [];
    return DefaultBillingGroupRecordDashboardElements;
  }, [fetchedLayoutConfig]);

  const renderElement = useCallback(
    (id: string, type: BillingGroupRecordDashboardElementType) => {
      switch (type) {
        case 'Divider':
          return (
            <Box pt="20px">
              <Divider component="div" />
            </Box>
          );
        case 'Spacer':
          return <></>;
        case 'BillingGroupName':
          return (
            <EditableSingleFieldElement
              isError={showValidation && !state.name}
              errorMessage="Required"
              fieldTitle="Name"
              fieldValue={state.name}
              fieldName="name"
              onChangeHandler={onStringFieldChange}
            />
          );
        case 'BillingGroupDescription':
          return (
            <EditableRichTextElement
              title="Summary"
              content={state.description}
              editorRef={editorRef}
              editorDirtyHandler={setIsEditorDirty}
            />
          );
        case 'BillingGroupClientName':
          return (
            <EditableSingleFieldElement format="custom" fieldTitle="Client">
              <ClientPicker
                hideLabel
                variant="standard"
                value={state.clientID}
                onSelectCallback={onClientChange}
                errorHandler={errorHandler}
                loadingHandler={loadingHandler}
                successHandler={successHandler}
              />
            </EditableSingleFieldElement>
          );
        case 'BillingGroupPrimaryContactName':
          return (
            <EditableSingleFieldElement format="custom" fieldTitle="Primary Contact">
              <ContactPicker
                hideLabel
                variant="standard"
                value={state.primaryContactID}
                clientId={state.clientID}
                onSelectCallback={(c) => onContactChange(c, 'primaryContactID')}
                errorHandler={errorHandler}
                loadingHandler={loadingHandler}
                successHandler={successHandler}
              />
            </EditableSingleFieldElement>
          );
        case 'BillingGroupAddress1':
          return (
            <EditableSingleFieldElement
              fieldTitle="Address 1"
              fieldValue={state.address1}
              fieldName="address1"
              onChangeHandler={onStringFieldChange}
            />
          );
        case 'BillingGroupAddress2':
          return (
            <EditableSingleFieldElement
              fieldTitle="Address 2"
              fieldValue={state.address2}
              fieldName="address2"
              onChangeHandler={onStringFieldChange}
            />
          );
        case 'BillingGroupAddress3':
          return (
            <EditableSingleFieldElement
              fieldTitle="Address 3"
              fieldValue={state.address3}
              fieldName="address3"
              onChangeHandler={onStringFieldChange}
            />
          );
        case 'BillingGroupSuburb':
          return (
            <EditableSingleFieldElement
              fieldTitle="Suburb"
              fieldValue={state.suburb}
              fieldName="suburb"
              onChangeHandler={onStringFieldChange}
            />
          );
        case 'BillingGroupState':
          if (state.country === 'AU') {
            return (
              <EditableSingleFieldElement
                fieldTitle="State"
                fieldValue={state.state}
                fieldName="state"
                onChangeHandler={onStringFieldChange}
                format="select"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="ACT">ACT</MenuItem>
                <MenuItem value="New South Wales">New South Wales</MenuItem>
                <MenuItem value="Northern Territory">Northern Territory</MenuItem>
                <MenuItem value="Queensland">Queensland</MenuItem>
                <MenuItem value="South Australia">South Australia</MenuItem>
                <MenuItem value="Tasmania">Tasmania</MenuItem>
                <MenuItem value="Victoria">Victoria</MenuItem>
                <MenuItem value="Western Australia">Western Australia</MenuItem>
              </EditableSingleFieldElement>
            );
          }
          if (state.country === 'NZ') {
            return (
              <EditableSingleFieldElement
                fieldTitle="Region"
                fieldValue={state.state}
                fieldName="state"
                onChangeHandler={onStringFieldChange}
                format="select"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Auckland">Auckland</MenuItem>
                <MenuItem value="Bay of Plenty">Bay of Plenty</MenuItem>
                <MenuItem value="Canterbury">Canterbury</MenuItem>
                <MenuItem value="Chatham Islands Territory">Chatham Islands Territory</MenuItem>
                <MenuItem value="Gisborne">Gisborne</MenuItem>
                <MenuItem value="Hawke's Bay">Hawke&apos;s Bay</MenuItem>
                <MenuItem value="Manawatu-Wanganui">Manawatu-Wanganui</MenuItem>
                <MenuItem value="Marlborough">Marlborough</MenuItem>
                <MenuItem value="Nelson">Nelson</MenuItem>
                <MenuItem value="Northland">Northland</MenuItem>
                <MenuItem value="Otago">Otago</MenuItem>
                <MenuItem value="Southland">Southland</MenuItem>
                <MenuItem value="Taranaki">Taranaki</MenuItem>
                <MenuItem value="Tasman">Tasman</MenuItem>
                <MenuItem value="Waikato">Waikato</MenuItem>
                <MenuItem value="Wellington">Wellington</MenuItem>
                <MenuItem value="West Coast">West Coast</MenuItem>
              </EditableSingleFieldElement>
            );
          }
          return (
            <EditableSingleFieldElement
              fieldTitle="State"
              fieldValue={state.state}
              fieldName="state"
              onChangeHandler={onStringFieldChange}
            />
          );
        case 'BillingGroupPostcode':
          return (
            <EditableSingleFieldElement
              fieldTitle="Postcode"
              fieldValue={state.postcode}
              fieldName="postcode"
              onChangeHandler={onStringFieldChange}
            />
          );
        case 'BillingGroupCountry':
          return (
            <EditableSingleFieldElement
              format="custom"
              fieldTitle="Country"
              isError={showValidation && !state.country}
              errorMessage="Required"
            >
              <CountryPicker
                hideLabel
                variant="standard"
                code={state.country}
                onSelectCallback={onCountryChange}
                error={showValidation && !state.country}
              />
            </EditableSingleFieldElement>
          );
      }
    },
    [
      state,
      billingGroupId,
      errorHandler,
      loadingHandler,
      successHandler,
      showValidation,
      onNumberFieldChange,
      theme.palette.mode,
      onStringFieldChange,
      positiveIntegerNumberValidator,
      onNumericListFieldChange,
      onContactChange,
      onCountryChange,
      onClientChange,
      onDateFieldChange,
      onDateTimeFieldChange,
    ]
  );

  const layoutResizeStopHandler = useCallback(() => setScreenResizedControl((prev) => !prev), []);

  useEffect(() => {
    const api = layoutRef.current;
    if (api && fetchedLayoutConfig) {
      const refreshTimeout = setTimeout(() => {
        api.refresh();
        api.refresh();
      }, 250);
      return () => clearTimeout(refreshTimeout);
    }
  }, [fetchedLayoutConfig, screenResizedControl]);

  useEffect(() => {
    window.addEventListener('resize', layoutResizeStopHandler);
    return () => window.removeEventListener('resize', layoutResizeStopHandler);
  }, [layoutResizeStopHandler]);

  const renderLayout = useCallback(() => {
    if (!fetchedLayoutConfig) return <></>;

    const sorted = [...elements];
    sorted.sort((a, b) => {
      if (a.col === b.col) return a.row < b.row ? -1 : 1;
      else return a.col < b.col ? -1 : 1;
    });

    const mediaQuery = mediaQueryMaxWidth ? `max-width: ${mediaQueryMaxWidth}` : undefined;
    return (
      <DashboardLayoutComponent
        cellSpacing={cellSpacing}
        columns={columns}
        cellAspectRatio={30 / 2}
        allowDragging={false}
        allowResizing={false}
        resizeStop={layoutResizeStopHandler}
        ref={(l) => (layoutRef.current = l)}
        mediaQuery={mediaQuery}
      >
        {sorted.map((e) => (
          <PanelWrapper
            key={e.id}
            id={e.id}
            col={e.col}
            row={e.row}
            sizeX={e.sizeX}
            sizeY={e.sizeY}
            minSizeX={e.minSizeX}
            minSizeY={e.minSizeY}
            maxSizeX={e.maxSizeX}
            maxSizeY={e.maxSizeY}
            resizeControl={screenResizedControl}
            resizeIconColor={theme.palette.text.disabled}
            gapX={gapX}
            gapY={gapY}
            unitWidth={unitWidth}
            unitHeight={unitHeight}
          >
            {renderElement(e.id, e.type)}
          </PanelWrapper>
        ))}
      </DashboardLayoutComponent>
    );
  }, [
    elements,
    fetchedLayoutConfig,
    theme.palette.text.disabled,
    screenResizedControl,
    layoutResizeStopHandler,
    renderElement,
  ]);

  return (
    <>
      {unsavedChangesDialog}
      {!hasBlockedRoute &&
        redirectBillingGroupId !== 0 &&
        savedChanges &&
        !hasChanges &&
        !isEditorDirty && <Navigate to={`/billing-groups/${redirectBillingGroupId}`} />}
      <Box p="10px" height="100%">
        <div className="control-section">{renderLayout()}</div>
      </Box>
    </>
  );
}
